import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { businessDateIn, todayIn } from '../lib/format';

/**
 * The seeded location, read once and shared by every screen that needs something off it.
 *
 * Kept as one query rather than one per caller: a second copy of this fetcher elsewhere in the
 * tree still hits the same cache entry today, but the day somebody changes the endpoint or the
 * key they change one of them, and the screens that read the other quietly stop agreeing about
 * which bar they are describing.
 */
function useSeedLocation() {
  const { data } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const res = await api.GET('/admin/v1/operations/locations');
      return res.data ?? [];
    },
  });
  return data?.[0];
}

/** Single-site business: the first (seeded) location is the working location everywhere. */
export function useLocationId(): string | undefined {
  return useSeedLocation()?.id;
}

/**
 * The currency the bar keeps its books in. Stock costs and register figures carry no currency of their
 * own, so the symbol beside them has to come from the location rather than a guess in the cell —
 * L 2,000 shown where $2,000 was meant is a twenty-fold error nobody can see on the screen.
 *
 * Falls back to the column default so the symbol does not flip while the location is still loading.
 */
export function useBaseCurrency(): string {
  return useSeedLocation()?.baseCurrency ?? 'USD';
}

/**
 * Which trading day it is at the bar right now — the default any "what happened today" screen
 * should open on.
 *
 * Read off the location rather than the browser, because the timezone and the hour the day rolls
 * at are both properties of the business, and the person reading may be nowhere near it. Undefined
 * until the location has loaded, so a caller can tell "not yet" from "today".
 */
export function useBusinessDate(): string | undefined {
  const location = useSeedLocation();
  if (!location) return undefined;
  return businessDateIn(location.timezone, location.dayCutoffMinutes ?? 240);
}

/**
 * The date to put on a piece of paper: a bill, a credit slip, an effective-from.
 *
 * Wall-clock at the bar, never the reader's browser — `todayIn` carries the reasoning and the six
 * times this has gone wrong. Unlike `useBusinessDate` this is never undefined: the timezone falls
 * back to the bar's own, which is the right answer for a single-site business and lets a form seed
 * a date field on its first render instead of showing an empty box until a query lands.
 */
export function useIslandToday(): string {
  return todayIn(useLocationTimezone());
}

/** The bar's timezone, for turning a picked day into the instants that bound it there. */
export function useLocationTimezone(): string {
  return useSeedLocation()?.timezone ?? 'America/Tegucigalpa';
}
